import { AppBar, Autocomplete, Box, Button, Chip, Paper, TextField, Toolbar, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import LoadingBackdrop from '../../Common/LoadingBackdrop';
import { Cancel, Save } from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApiKeys } from '../../../hooks/useApiKeys';
import TokenSecretDialog from './TokenSecretDialog';
import { formatDate } from './ApiKeysPage';
import MainContentContainer from '../../MainContentContainer/MainContentContainer';

const EXPIRATION_OPTIONS = [
    { value: 'never', label: 'Never' },
    { value: '30', label: '30 days' },
    { value: '60', label: '60 days' },
    { value: '90', label: '90 days' },
    { value: '180', label: '6 months' },
    { value: '365', label: '1 year' },
];

function computeExpiresAt(option) {
    if (option === 'never') return null;
    const date = new Date();
    date.setDate(date.getDate() + parseInt(option));
    return date.toISOString().slice(0, 19).replace('T', ' ');
}

export default function ApiKeyFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const { loading, fetchOne, create, update, availableScopes, fetchAvailableScopes } = useApiKeys();

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        abilities: [],
        expiration: 'never',
    });
    // Saved expiry, shown read-only on edit.
    const [currentExpiresAt, setCurrentExpiresAt] = useState(null);
    const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
    const [plainTextToken, setPlainTextToken] = useState('');

    useEffect(() => {
        fetchAvailableScopes();
        if (isEdit) {
            loadToken();
        }
    }, []);

    const loadToken = async () => {
        const data = await fetchOne(id);
        if (data) {
            setFormData({
                name: data.name || '',
                description: data.description || '',
                abilities: Array.isArray(data.abilities) ? data.abilities.filter(a => a !== '*') : [],
                expiration: 'never',
            });
            setCurrentExpiresAt(data.expires_at || null);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = async (e) => {
        e.preventDefault();

        const payload = {
            name: formData.name,
            description: formData.description,
            abilities: formData.abilities.length > 0 ? formData.abilities : null,
        };
        // Edit omits expires_at: APIv2 keeps the existing expiry when the
        // key is absent, while null would remove it.
        if (!isEdit) payload.expires_at = computeExpiresAt(formData.expiration);

        if (isEdit) {
            const result = await update(id, payload);
            if (result) {
                navigate('/api-keys');
            }
        } else {
            const result = await create(payload);
            if (result) {
                setPlainTextToken(result.plain_text_token);
                setTokenDialogOpen(true);
            }
        }
    };

    const handleTokenDialogClose = () => {
        setTokenDialogOpen(false);
        setPlainTextToken('');
        navigate('/api-keys');
    };

    return (
        <MainContentContainer component="form" onSubmit={handleSave}>
            <LoadingBackdrop open={loading} />
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        {isEdit ? 'Edit' : 'Create'} API Key
                    </Typography>
                    <Button
                        color="success"
                        type="submit"
                        startIcon={<Save />}
                        variant="contained"
                        sx={{ marginRight: 2 }}
                        disabled={loading}
                    >
                        {isEdit ? 'Save' : 'Create'}
                    </Button>
                    <Button
                        color="error"
                        component={Link}
                        to="/api-keys"
                        startIcon={<Cancel />}
                        variant="contained"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                </Toolbar>
            </AppBar>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                        fullWidth
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        inputProps={{ maxLength: 255 }}
                    />

                    <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        multiline
                        rows={3}
                        inputProps={{ maxLength: 1000 }}
                    />

                    <Autocomplete
                        multiple
                        options={availableScopes}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.scope}
                        isOptionEqualToValue={(option, value) => {
                            const optScope = typeof option === 'string' ? option : option.scope;
                            const valScope = typeof value === 'string' ? value : value.scope;
                            return optScope === valScope;
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.scope}>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.scope}</Typography>
                                    <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                                </Box>
                            </li>
                        )}
                        value={availableScopes.filter(s => formData.abilities.includes(s.scope))}
                        onChange={(_, newValue) => setFormData({ ...formData, abilities: newValue.map(v => typeof v === 'string' ? v : v.scope) })}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip variant="outlined" label={option.scope} {...getTagProps({ index })} key={index} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Scopes"
                                placeholder="Select scopes"
                                helperText="Leave empty for full access (wildcard)"
                            />
                        )}
                    />

                    {isEdit ? (
                        <TextField
                            fullWidth
                            label="Expiration"
                            value={formatDate(currentExpiresAt)}
                            InputProps={{ readOnly: true }}
                            helperText="Editing keeps the key's current expiry."
                        />
                    ) : (
                        <FormControl fullWidth>
                            <InputLabel>Expiration</InputLabel>
                            <Select
                                name="expiration"
                                value={formData.expiration}
                                onChange={handleChange}
                                label="Expiration"
                            >
                                {EXPIRATION_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Paper>
            </Box>

            <TokenSecretDialog
                open={tokenDialogOpen}
                plainTextToken={plainTextToken}
                onClose={handleTokenDialogClose}
            />
        </MainContentContainer>
    );
}
